import config from "../config";
import { environment, collectionPath } from "../context/EnvContext";

class ApiClient {
  constructor() {
    this.baseUrl = config.apiConfig.API_ENDPOINT_URL;
    this.defaultHeaders = {
      "Content-Type": "application/json",
      "x-environment": environment,
      "x-collection-path": collectionPath,
    };
  }

  /**
   * Get authorization headers for authenticated requests
   * @param {Object} user - User object containing accessToken
   * @returns {Object} Headers object with authorization
   */
  getAuthHeaders(user) {
    const headers = { ...this.defaultHeaders };
    
    if (user?.accessToken) {
      headers.Authorization = `Bearer ${user.accessToken}`;
    }
    
    return headers;
  }

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} user - User object for authentication
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Response>}
   */
  async get(endpoint, user = null, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = user ? this.getAuthHeaders(user) : this.defaultHeaders;
    
    return fetch(url, {
      method: "GET",
      headers,
      ...options,
    });
  }

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} data - Request body data
   * @param {Object} user - User object for authentication
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Response>}
   */
  async post(endpoint, data = {}, user = null, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = user ? this.getAuthHeaders(user) : this.defaultHeaders;
    
    return fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
      ...options,
    });
  }

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} data - Request body data
   * @param {Object} user - User object for authentication
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Response>}
   */
  async put(endpoint, data = {}, user = null, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = user ? this.getAuthHeaders(user) : this.defaultHeaders;
    
    return fetch(url, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
      ...options,
    });
  }

  /**
   * Make a PATCH request
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} data - Request body data
   * @param {Object} user - User object for authentication
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Response>}
   */
  async patch(endpoint, data = {}, user = null, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = user ? this.getAuthHeaders(user) : this.defaultHeaders;
    
    return fetch(url, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
      ...options,
    });
  }

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} user - User object for authentication
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Response>}
   */
  async delete(endpoint, user = null, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = user ? this.getAuthHeaders(user) : this.defaultHeaders;
    
    return fetch(url, {
      method: "DELETE",
      headers,
      ...options,
    });
  }

  /**
   * Handle API response with automatic JSON parsing and error handling
   * @param {Response} response - Fetch response object
   * @returns {Promise<Object>} Parsed JSON response
   * @throws {Error} If response is not ok or JSON parsing fails
   */
  async handleResponse(response) {
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        // If JSON parsing fails, use the default error message
      }
      
      throw new Error(errorMessage);
    }
    
    return response.json();
  }

  /**
   * Convenience method for GET requests that automatically handles the response
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} user - User object for authentication
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Object>} Parsed JSON response
   */
  async getData(endpoint, user = null, options = {}) {
    const response = await this.get(endpoint, user, options);
    return this.handleResponse(response);
  }

  /**
   * Convenience method for POST requests that automatically handles the response
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} data - Request body data
   * @param {Object} user - User object for authentication
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Object>} Parsed JSON response
   */
  async postData(endpoint, data = {}, user = null, options = {}) {
    const response = await this.post(endpoint, data, user, options);
    return this.handleResponse(response);
  }
}

// Export a singleton instance
const apiClient = new ApiClient();
export default apiClient;